import { useEffect, useMemo, useRef } from 'react';
import { useAnimations, useGLTF } from '@react-three/drei';
import * as THREE from 'three';
import { SkeletonUtils } from 'three-stdlib';
import {
  ARCHETYPE_URLS,
  RIGGED_FOOTBALLER_URL,
  type FootballerArchetype,
} from '../../lib/useRiggedFootballer';

/**
 * Skinned-mesh footballer loaded from a GLB at /footballer-rigged.glb.
 * Activated by useRiggedFootballer's runtime probe — when the file is
 * absent (default), PlayerToken3D renders ProceduralFootballer instead.
 *
 * Expected GLB conventions (from a Blender / Mixamo / commissioned export):
 *   - Single armature with a skinned mesh
 *   - Material slots named (case-insensitive substring match):
 *       "jersey" → recoloured to teamHex
 *       "shorts" → recoloured to teamHex × HSL-0.22
 *       "socks"  → recoloured to teamHex × HSL+0.06
 *       "skin", "hair", "boot" → left as authored
 *   - Animation clips named "Idle", "Walk", "Run", "Kick" (substring
 *     match also tolerates "soccer_idle", "Mixamo:Idle" etc).
 *
 * If clips are missing, the figure stands still — no crash.
 */
/** Gait the figure should play. Driven by the parent based on whether
 *  this token is being scrubbed (walk) or auto-played (run) along a
 *  bound run-path; otherwise idle. */
export type Gait = 'idle' | 'walk' | 'run';

interface Props {
  teamHex: string;
  selected: boolean;
  /** Phase offset 0..2π so a row of identical-clip players don't tick
   *  in unison. Applied to the action's `time` on first play. */
  idlePhase: number;
  /**
   * Active gait. 'idle' = ambient. 'walk' = manual scrub of the run
   * scrubber (slower step). 'run' = auto-play (sprint). The component
   * cross-fades smoothly between any two clips when this prop changes.
   */
  gait?: Gait;
  /**
   * Body archetype to load. Defaults to 'midfielder' (the canonical
   * /footballer-rigged.glb). Other values pull a sibling GLB so the
   * squad reads as varied silhouettes.
   */
  archetype?: FootballerArchetype;
  /** Mirrors `prefers-reduced-motion: reduce`. When true the active
   *  animation clip plays the first frame and then pauses, so the
   *  rigged figure stands still rather than breathing/jogging. */
  reducedMotion?: boolean;
}

function shade(hex: string, amount: number): string {
  const c = new THREE.Color(hex);
  const hsl = { h: 0, s: 0, l: 0 };
  c.getHSL(hsl);
  hsl.l = Math.max(0, Math.min(1, hsl.l + amount));
  return new THREE.Color().setHSL(hsl.h, hsl.s, hsl.l).getHexString();
}

export function RiggedFootballer({ teamHex, selected, idlePhase, gait = 'idle', archetype = 'midfielder', reducedMotion = false }: Props) {
  const groupRef = useRef<THREE.Group>(null);
  const url = ARCHETYPE_URLS[archetype] ?? RIGGED_FOOTBALLER_URL;
  const { scene, animations } = useGLTF(url);

  // Clone the loaded scene per-instance so 22 figures don't share a
  // single skeleton + mesh. THREE.Object3D.clone() does NOT deep-copy
  // the skeleton bones — naive cloning means every instance shares the
  // same skinned mesh, so the first animation tick warps every figure
  // to one shared pose and the rest collapse to NaN. SkeletonUtils.clone
  // walks the bone tree and rebinds the cloned skin to the cloned bones.
  // BUT — clone() leaves materials as shared references, so any kit
  // recolouring would bleed across all 22 figures (last-write-wins).
  // We deep-clone every material on the cloned skinned meshes too.
  const cloned = useMemo(() => {
    const c = SkeletonUtils.clone(scene);
    c.traverse((node) => {
      const mesh = node as THREE.SkinnedMesh & THREE.Mesh;
      if (!mesh.isMesh && !(mesh as THREE.SkinnedMesh).isSkinnedMesh) return;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      // Skinned meshes report stale local bounds during animation; disable
      // frustum culling so they don't pop in/out as bones move them.
      mesh.frustumCulled = false;
      // Clone materials so kit recolouring is per-instance, not shared.
      const orig = mesh.material as THREE.MeshStandardMaterial | THREE.MeshStandardMaterial[];
      const cloneOne = (m: THREE.Material) => (m as THREE.MeshStandardMaterial).clone();
      const cloned = Array.isArray(orig) ? orig.map(cloneOne) : cloneOne(orig);
      mesh.material = cloned as THREE.Material | THREE.Material[];
      const apply = (m: THREE.Material) => {
        const standard = m as THREE.MeshStandardMaterial;
        const name = (standard.name ?? '').toLowerCase();
        if (name.includes('jersey')) standard.color.setStyle(teamHex);
        else if (name.includes('shorts')) standard.color.setStyle(`#${shade(teamHex, -0.22)}`);
        else if (name.includes('sock')) standard.color.setStyle(`#${shade(teamHex, 0.06)}`);
      };
      if (Array.isArray(cloned)) cloned.forEach(apply);
      else if (cloned) apply(cloned);
    });
    return c;
  }, [scene, teamHex]);

  // Animations are loaded by useAnimations but never played — coaches
  // reported the bob + cross-faded walk/run cycle reading as a "weird
  // FIFA-2003 gait" rather than as useful motion. The figure stays in
  // its bind pose (arms relaxed at the sides per the joint layout in
  // generate-footballer.py), and during run-path playback the parent
  // translates the entire group along the curve — figures slide like
  // chess pieces, no leg cycle, no idle bob.
  // We still call useAnimations so the AnimationMixer is hooked up and
  // the bone hierarchy is wired; we just don't kick any action off.
  useAnimations(animations, cloned);
  // gait + reducedMotion + idlePhase props kept for API parity with
  // PlayerToken3D but currently unused. The clips remain in the GLB if
  // a future surface wants to bring them back.
  void gait;
  void reducedMotion;
  void idlePhase;

  // Selection emissive — same idea as ProceduralFootballer's. We walk
  // jersey/shorts materials and flip emissiveIntensity.
  useEffect(() => {
    const intensity = selected ? 0.32 : 0;
    cloned.traverse((node) => {
      const mesh = node as THREE.Mesh;
      if (!mesh.isMesh) return;
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const m of mats) {
        const std = m as THREE.MeshStandardMaterial;
        if (!std?.name) continue;
        const n = std.name.toLowerCase();
        if (n.includes('jersey') || n.includes('shorts')) {
          std.emissive.setStyle(teamHex);
          std.emissiveIntensity = n.includes('jersey') ? intensity : intensity * 0.5;
        }
      }
    });
  }, [selected, teamHex, cloned]);

  return (
    // Inner group offsets the figure down so the boot bottom (y≈0.02 in
    // object space) rests on the grass at world y=0 instead of hovering
    // 3cm above. -0.02 in object space → -0.032 world after the parent's
    // 1.6× scale. Procedural doesn't need this — its boot meshes already
    // straddle y=0 — so the offset lives here, not at PlayerToken3D.
    <group ref={groupRef} position={[0, -0.02, 0]}>
      <primitive object={cloned} />
    </group>
  );
}
